import Log from './log';
import LocalEvent from './local-event';
import { sdkEntityToCugContact } from './contact-util';

/**
 * This class is used to communicate with the WebChat SDK.
 */
class SdkClient {
    static getInstance() {
        if (typeof SdkClient.instance !== 'undefined') {
            return SdkClient.instance;
        }

        SdkClient.instance = new SdkClient();

        return SdkClient.instance;
    }

    constructor() {
        this.authService = null;
    }

    /**
     * (Auto)create the SDK connection.
     * @param {string} sdkEventManagerContainerId The id of the container who will carry the SDK internal events.
     * The function return a promise which will resolve with a boolean that represent if the guest mode (user not authenticated) is on or off.
     * @return Promise<boolean>
     */
    init(sdkEventManagerContainerId) {
        return new Promise((resolve) => {
            if (!wcfUiCfg.swapiToken) {
                Log.debug('SDK CLIENT', 'There is no token that needs to be validate. Ready to authenticate.');
                LocalEvent.trigger(LocalEvent.eventTypes.SDK_CLIENT_READY_TO_AUTHENTICATE);

                // guest
                resolve(true);

                return;
            }

            Log.debug('SDK CLIENT', 'Attempting to validate the user/token and start the web socket.');
            webchatSDK.STWEventManager.setContainer(document.getElementById(sdkEventManagerContainerId));
            webchatSDK.STWLoginManager.init(wcfUiCfg.swapiUrlBase, true);
            webchatSDK.STWLoginManager._loginWithTokenFromUI(wcfUiCfg.swapiToken).then(
                (response) => {
                    Log.debug(
                        'SDK CLIENT',
                        `The user ${wcfUiCfg.msisdn} and its token has been validated. Loading user data ...`,
                    );

                    webchatSDK.STWAccountManager.getUser()
                        .then((sdkEntity) => {
                            LocalEvent.trigger(LocalEvent.eventTypes.SDK_CLIENT_AUTHENTICATED, {
                                ...sdkEntityToCugContact(sdkEntity),
                                organizationId: response.data.results.organizationIdentifier,
                                operationalStatusList: response.data.results.operationalStatusList,
                            });

                            // guest mode off
                            resolve(false);
                        })
                        .catch((error) => {
                            Log.error(
                                'SDK CLIENT',
                                `There was an error while loading logged in user data: ${JSON.stringify(error)}.`,
                            );

                            // guest mode on
                            resolve(true);
                        });
                },
                (err) => {
                    Log.warn('SDK CLIENT', `The user ${wcfUiCfg.msisdn} was not validated due to error: ${err.code}`);
                    LocalEvent.trigger(LocalEvent.eventTypes.SDK_CLIENT_READY_TO_AUTHENTICATE);

                    // guest mode on
                    resolve(true);
                },
            );
        });
    }

    /**
     * Get the CUG settings (rare and frequent).
     * @return Promise<CugSettings>
     */
    getCugSettings() {
        return new Promise((resolve) => {
            webchatSDK.STWAPI.getCugSettings().then((res) => {
                if (!res.data || !res.data.results.CugData) {
                    Log.error('SDK CLIENT', 'There was an error while  loading CUG Settings');

                    resolve({});

                    return;
                }

                resolve(JSON.parse(res.data.results.CugData));
            });
        });
    }

    /**
     * Get the CUG entities: cug_subscribers,cug_groups,cug_groups_membership
     * @param {CugEntityType[]} entities Can be from this array: cug_subscribers,cug_groups,cug_groups_membership
     * @return Promise<CugData>
     */
    getCugEntities(entities = []) {
        return new Promise((resolve) => {
            webchatSDK.STWAPI.getCugEntities(entities).then((res) => {
                if (!res.data || !res.data.results.CugData) {
                    Log.error('SDK CLIENT', 'There was an error while  loading CUG Settings');

                    resolve({});

                    return;
                }

                resolve(JSON.parse(res.data.results.CugData));
            });
        });
    }

    /**
     * Get the Configuration settings that are accessible at subscriber level (and cache them).
     * @param {boolean} useCachedDataIfAny
     * @return Promise<ConfigurationSettings>
     */
    getConfigurationSettings(useCachedDataIfAny = true) {
        return new Promise((resolve, reject) => {
            if (useCachedDataIfAny && 'cachedConfigurationSettings' in this) {
                resolve(this.cachedConfigurationSettings);

                return;
            }

            webchatSDK.STWAPI.getConfigurationSettings()
                .then((sdkResponse) => {
                    const configurationSettings = sdkResponse.data.results;

                    this.cachedConfigurationSettings = configurationSettings;

                    resolve(this.cachedConfigurationSettings);
                })
                .catch((err) => {
                    Log.warn('SDK CLIENT', `There was an error while loading configuration settings: ${err.code}`);
                    reject(err);
                });
        });
    }

    /**
     * Get the Voip config
     * @return Promise<VoipConfig>
     */
    getVoipConfig() {
        return new Promise((resolve, reject) => {
            webchatSDK.STWAPI.getConfigServices()
                .then((response) => {
                    resolve(response.data.results);
                })
                .catch((e) => {
                    reject(e);
                });
        });
    }

    /**
     * Get a new voip token.
     * @return Promise<VoipToken>
     */
    getVoipToken() {
        return new Promise((resolve, reject) => {
            webchatSDK.STWAPI.getVoipToken()
                .then((response) => {
                    resolve(response.data.results.VoipToken);
                })
                .catch((e) => {
                    reject(e);
                });
        });
    }

    /**
     * Set a listener for the events received over the web socket.
     * @param {string} eventType
     * @param {Function} callback
     */
    setSocketEventListener(eventType, callback) {
        webchatSDK.Socket.setEventListener(eventType, callback);
    }

    removeSocketEventListener(eventType, callback) {
        webchatSDK.Socket.removeEventListener(eventType, callback);
    }

    /**
     * Emit an event over the web-socket
     * @param {string} eventType
     * @param {Object} params The payload to be added in the event.
     * @param {boolean} disableLog If the SDK should write that in its log por not.
     */
    emitSocketEvent(eventType, params = {}, disableLog = false) {
        if (!disableLog) {
            Log.debug('SDK CLIENT', `Emitting socket event: ${eventType}  with params ${Log.stringify(params)}`);
        }

        webchatSDK.Socket.emitEvent(eventType, params, disableLog);
    }

    /**
     * Set a listener for the INTERNAL events manipulated by the SDK.
     * @param {string} eventType
     * @param {Function} callback
     */
    setEventManagerListener(eventType, callback) {
        webchatSDK.STWEventManager.setListener(eventType, callback);
    }
}

export default SdkClient.getInstance();
